import React, { useState, useEffect } from "react";
import '../../CSS/Consulat_honoraires.css';
import { FaSearch } from "react-icons/fa";
import { useLanguageData } from '../../hooks/useLanguageData';
import { useTranslation } from 'react-i18next';

const Consulat = () => {
  const [search, setSearch] = useState("");
  const [consulatsData, setConsulatsData] = useState([]);
  const [groupedConsulats, setGroupedConsulats] = useState({});
  const { t } = useTranslation();
  
  // Utiliser useLanguageData pour charger les consulats
  const { 
    data: consulatsResponse, 
    loading, 
    error 
  } = useLanguageData('/api/v1/embassy/consuls');

  // Transformer les données quand elles arrivent
  useEffect(() => {
    if (consulatsResponse?.data && Array.isArray(consulatsResponse.data)) {
      // console.log("🔍 Données API consulats (avec langue):", consulatsResponse);
      
      const individualConsuls = consulatsResponse.data.map(consul => ({
        id: consul.id || Math.random(),
        country: consul.country,
        chef: `${consul.civility} ${consul.name}`,
        address: consul.adress,
        tel: consul.phone,
        fax: consul.fax,
        email: consul.email,
        flagCountry: consul.flagCountry 
      }));

      // Trier par pays en ordre alphabétique français
      const sortedConsuls = individualConsuls.sort((a, b) => {
        const countryA = a.country || '';
        const countryB = b.country || '';
        
        return countryA.localeCompare(countryB, 'fr', {
          sensitivity: 'base',
          ignorePunctuation: true,
          numeric: true
        });
      });

      setConsulatsData(sortedConsuls);
    }
  }, [consulatsResponse]);

  // Grouper les consulats par pays
  useEffect(() => {
    const grouped = {};
    consulatsData.forEach(consulat => {
      const country = consulat.country || 'Autres';
      if (!grouped[country]) {
        grouped[country] = [];
      }
      grouped[country].push(consulat);
    });
    setGroupedConsulats(grouped);
  }, [consulatsData]);

  // Fonction pour obtenir le drapeau du pays
  const getCountryFlag = (consulat) => {
    if (consulat.flagCountry) {
      return (
        <img
          src={consulat.flagCountry}
          alt={`${consulat.country}`}
          style={{
            width: '100px',
            height: 'auto',
            marginRight: '12px',
            borderRadius: '0px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            objectFit: 'cover'
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.style.display = 'none';
          }}
        />
      );
    }
    return null;
  };

  // Filtrer les consulats par pays (recherche textuelle)
  const filteredGroupedConsulats = {};
Object.keys(groupedConsulats).forEach(country => {
  const filtered = groupedConsulats[country].filter(c =>
    search ? (
      c.country?.toLowerCase().includes(search.toLowerCase()) ||
      c.chef?.toLowerCase().includes(search.toLowerCase()) ||
      c.address?.toLowerCase().includes(search.toLowerCase()) ||
      c.tel?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase())
    ) : true
  );
  if (filtered.length > 0) {
    filteredGroupedConsulats[country] = filtered;
  }
});

  // Composant de spinner de chargement
  const LoadingSpinner = () => (
    <div style={{
      textAlign: 'center',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        width: "50px",
        height: "50px",
        border: "4px solid #f3f3f3",
        borderTop: "4px solid #1a3c5e",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
        marginBottom: "1rem"
      }}></div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );

  return (
    <div className="consulat-layout my-5">
      {/* Colonne gauche - Recherche */}
      <div className="search-column">
        <h1 className="search-title">{t('Consul.descrip')}</h1>
        <div
          className="search-input-container"
          style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}
        >
          <input
            type="text"
            id="search-consulat"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="RECHERCHER PAR PAYS, NOM..."
            style={{
              width: '100%',
              padding: '0.9rem 1rem 0.9rem 2.8rem',
              fontSize: '1rem',
              border: '1px solid #ccc',
              borderBottom: "3px solid #ffbe00",
              borderRadius: '0px',
              outline: 'none',
              boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
              backgroundColor: 'white',
            }}
          />
          <FaSearch
            style={{
              position: 'absolute',
              left: '0.9rem',
              fontSize: '1.2rem',
              color: '#0d0d0dff',
              pointerEvents: 'none',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          />
        </div>
      </div>

      <div className="results-column">
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="no-data-message">
            <img
              src="https://paris.diplomatie.bj/images/icon_illustration_ambassade.svg"
              loading="lazy"
              alt="Icône ambassadeur"
              style={{ height: "300px", marginBottom: '25px' }}
            />
            <p style={{ color: "black", fontWeight: '300' }}>
              <strong>
                Erreur de chargement des consulats
              </strong>
            </p>
          </div>
        ) : Object.keys(filteredGroupedConsulats).length === 0 ? (
          <div className="no-data-message">
            <img
              src="https://paris.diplomatie.bj/images/icon_illustration_ambassade.svg"
              loading="lazy"
              alt="Icône ambassadeur"
              style={{ height: "300px", marginBottom: '25px' }}
            />
            <p style={{ color: "black", fontWeight: '300' }}>
              <strong>
                {search ?
                  `Aucun consulat trouvé pour "${search}"` :
                  "Aucun consulat trouvé"
                }
              </strong>
            </p>
          </div>
        ) : (
          <div className="consulats-list">
            {Object.entries(filteredGroupedConsulats).map(([country, consulats]) => (
              <div key={country} className="consulat-country-group">
                {/* Titre du pays au-dessus du groupe */}
                <div className="country-group-header">
                  <h2 className="country-group-title">
                    {country}
                  </h2>
                </div>

                {/* Grille des consulats pour ce pays */}
                <div className="consulats-grid-container">
                  {consulats.map((consulat, index) => (
                    <div key={consulat.id || index} className="consulat-card-separate">
                      {/* Chaque bloc conserve son en-tête de pays avec drapeau */}
                      <div className="country-header">
                        <span className="country-flag">{getCountryFlag(consulat)}</span>
                        <h2 className="country-name">{consulat.country}</h2>
                      </div>

                      {/* Informations du consulat */}
                      <div className="consul-details">
                        {consulat.chef && consulat.chef !== "Information non disponible" && (
                          <div className="consul-name-grid">
                            {consulat.chef}
                          </div>
                        )}

                        {consulat.address && consulat.address !== "Information non disponible" && (
                          <div className="consul-info-grid">
                            <i className="icon-outline location-icon"></i>
                            <span>{consulat.address}</span>
                          </div>
                        )}

                        {consulat.tel && consulat.tel !== "Information non disponible" && (
                          <div className="consul-info-grid">
                            <i className="icon-outline phone-icon"></i>
                            <div className="phones-container">
                              {consulat.tel.split(';').map((phone, phoneIndex) => (
                                <div key={phoneIndex} className="phone-number">
                                  {phone.trim()}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {consulat.email && consulat.email !== "Information non disponible" && (
                          <div className="consul-info-grid">
                            <i className="icon-outline email-icon"></i>
                            <span>{consulat.email}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Consulat;